import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

// ---------------------------------------------------------------------------
// NEWS
// Content lives in `content/news/*.md`. To add an update, drop in a new
// markdown file with `date` (ISO, used for ordering) and `dateLabel` (the text
// shown on the left) in the frontmatter, and the update itself as the body.
// Newest `date` renders first.
// ---------------------------------------------------------------------------
const StyledNewsSection = styled.section`
  max-width: 900px;

  ul.news-list {
    ${({ theme }) => theme.mixins.resetList};
    margin: 0;
  }

  li.news-item {
    display: grid;
    grid-template-columns: 140px 1fr;
    grid-gap: 24px;
    align-items: baseline;
    padding: 16px 0;
    border-bottom: 1px solid var(--lightest-navy);

    &:first-of-type {
      padding-top: 0;
    }

    @media (max-width: 600px) {
      grid-template-columns: 100px 1fr;
      grid-gap: 14px;
    }

    .news-date {
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      font-variant-numeric: tabular-nums;
    }

    .news-text {
      /* Sits on the page background, so it uses the same tokens as body copy
         (--slate) and headings/emphasis (--lightest-slate) — matching About
         and the job bullets, and staying readable whatever the palette is. */
      color: var(--slate);
      font-size: var(--fz-lg);

      p {
        margin: 0;
      }

      strong {
        color: var(--lightest-slate);
        font-weight: 600;
      }

      @media (max-width: 600px) {
        font-size: var(--fz-md);
      }
    }
  }
`;

const News = () => {
  const data = useStaticQuery(graphql`
    query {
      news: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/news/" } }
        sort: { frontmatter: { date: DESC } }
      ) {
        edges {
          node {
            frontmatter {
              date
              dateLabel
            }
            html
          }
        }
      }
    }
  `);

  const newsItems = data.news.edges;

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledNewsSection id="news" ref={revealContainer}>
      <h2 className="numbered-heading">News</h2>

      <ul className="news-list">
        {newsItems &&
          newsItems.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { date, dateLabel } = frontmatter;

            return (
              <li className="news-item" key={i}>
                <span className="news-date">{dateLabel || date}</span>
                <span
                  className="news-text"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </li>
            );
          })}
      </ul>
    </StyledNewsSection>
  );
};

export default News;
